/**
 * Market Insights — customer-facing analytics client (Phase 7).
 *
 * Two surfaces, both server-gated by admin toggles:
 *  - PUBLIC area insights (no login) — /insights/areas. When the publicInsights
 *    toggle is off the backend 404s, so getPublicAreas() returns
 *    { available:false } and the page shows a graceful "not available" state.
 *  - SELF-SERVICE broker/company scorecards — /me/insights/{broker,company}.
 *    The backend returns { available:false, reason } (200) when the feature is
 *    off or the caller isn't a broker/company manager, so the account UI just
 *    hides the widget.
 */

import { apiRequest, ApiError } from '../utils/apiClient';

export type DataLevel = 'curated' | 'activity' | 'minimal';

export interface AreaPriceBucket {
  count: number;
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
}

export interface PublicAreaInsight {
  areaId: string;
  name: string;
  nameAr: string | null;
  city: string | null;
  listings: number;
  price: Partial<Record<string, AreaPriceBucket>>; // keyed by listingType (SALE/RENT)
  demandBand?: 'low' | 'medium' | 'high';
  activityBand?: 'low' | 'medium' | 'high';
  soldRecently?: boolean;
  activityScore?: number;
  demandScore?: number;
  views90d?: number;
  contacts90d?: number;
  sold90d?: number;
  newListings90d?: number;
}

export interface PublicAreasResult {
  available: boolean;
  level: DataLevel;
  areas: PublicAreaInsight[];
}

export async function getPublicAreas(cityId?: string): Promise<PublicAreasResult> {
  try {
    const q = cityId ? `?cityId=${encodeURIComponent(cityId)}` : '';
    const res = await apiRequest<{ level: DataLevel; areas: PublicAreaInsight[] }>(`/insights/areas${q}`);
    return { available: true, level: res.level, areas: res.areas || [] };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { available: false, level: 'curated', areas: [] };
    }
    throw err;
  }
}

export async function getPublicArea(id: string): Promise<{ available: boolean; level: DataLevel; area: PublicAreaInsight | null }> {
  try {
    const res = await apiRequest<{ level: DataLevel; area: PublicAreaInsight }>(`/insights/areas/${encodeURIComponent(id)}`);
    return { available: true, level: res.level, area: res.area };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { available: false, level: 'curated', area: null };
    throw err;
  }
}

// --- self-service ------------------------------------------------------------
export interface EngagementTotals {
  propertyViews?: number; contacts?: number; calls?: number; whatsapps?: number;
  emails?: number; shares?: number; favorites?: number; inquiries?: number;
  newListings?: number; soldCount?: number;
}
export interface TrendPoint { date: string; views: number; contacts: number; inquiries: number }

export interface BrokerInsights {
  available: boolean;
  reason?: string;
  period?: string;
  agent?: { id: string; name: string; company: string | null; avgRating: number | null; totalReviews: number | null; badge: string | null; totalListings: number | null; totalSold: number | null; totalRented: number | null };
  engagement?: EngagementTotals;
  listings?: { total: number; active: number; sold: number; rented: number; pctVideo: number; pctTour: number };
  trend?: TrendPoint[];
}

export interface CompanyEmployee { agentId: string; name: string; rating: number | null; views: number; contacts: number; inquiries: number; newListings: number; sold: number }
export interface CompanyInsights {
  available: boolean;
  reason?: string;
  period?: string;
  company?: { id: string; name: string; nameAr: string | null; isVerified: boolean; teamSize: number };
  engagement?: EngagementTotals;
  listings?: { total: number; active: number; sold: number; rented: number };
  employees?: CompanyEmployee[];
  trend?: TrendPoint[];
}

export async function getMyBrokerInsights(token: string, period = '30d'): Promise<BrokerInsights> {
  return apiRequest<BrokerInsights>(`/me/insights/broker?period=${period}`, { token });
}

export async function getMyCompanyInsights(token: string, period = '30d'): Promise<CompanyInsights> {
  return apiRequest<CompanyInsights>(`/me/insights/company?period=${period}`, { token });
}
